import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface ExpandableTextProps {
  text: string;
  clampLines?: number;
  className?: string;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  clampLines = 3,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure overflow in clamped state
    const check = () => {
      const wasExpanded = expanded;
      if (wasExpanded) return;
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, expanded]);

  const clampClass =
    clampLines === 2 ? 'line-clamp-2' :
    clampLines === 4 ? 'line-clamp-4' :
    clampLines === 5 ? 'line-clamp-5' :
    'line-clamp-3';

  return (
    <div className={className}>
      <p
        ref={ref}
        className={cn(
          'text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed',
          !expanded && clampClass
        )}
      >
        {text}
      </p>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
